import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
// import 'leaflet/dist/leaflet.css';
import { 
  ArrowLeft, MapPin, Plus, X, Calendar, DollarSign, Users, 
  FileText, AlertCircle, Target, Building2, Search, ChevronRight,
  Save, Clock, Upload, Link as LinkIcon, Store, Home, Coffee,
  Building, CheckCircle2, Edit2, Trash2, User, AlertTriangle,
  CheckCircle, Clock3, Ban, ChevronDown
} from 'lucide-react';

// Delete default Leaflet icon configuration
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Constants and Configurations
const SITE_TYPES = [
  { 
    id: 'standalone', 
    icon: Building, 
    label: 'Standalone Building',
    description: 'Independent retail location' 
  },
  { 
    id: 'mall', 
    icon: Store, 
    label: 'Mall Shop',
    description: 'Retail space within a shopping mall' 
  },
  { 
    id: 'kiosk', 
    icon: Coffee, 
    label: 'Kiosk',
    description: 'Small retail kiosk or booth' 
  },
  { 
    id: 'shop', 
    icon: Home, 
    label: 'Street Shop',
    description: 'Shop in a commercial street' 
  }
];

const FORM_STEPS = [
  'Basic Info',
  'Location',
  'Budget',
  'Team',
  'Timeline'
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }
];

const PROJECT_TYPES = [
  { value: 'renovation', label: 'Renovation' },
  { value: 'new-construction', label: 'New Construction' },
  { value: 'relocation', label: 'Relocation' },
  { value: 'expansion', label: 'Expansion' }
];

const BUDGET_CATEGORIES = [
  { id: 'construction', label: 'Construction Cost', percentage: 60 },
  { id: 'design', label: 'Design & Engineering', percentage: 10 },
  { id: 'equipment', label: 'Equipment & Fixtures', percentage: 15 },
  { id: 'permits', label: 'Permits & Licenses', percentage: 5 },
  { id: 'contingency', label: 'Contingency', percentage: 10 }
];

const TEAM_ROLES = [
  { id: 'project_manager', label: 'Project Manager', required: true },
  { id: 'architect', label: 'Architect', required: true },
  { id: 'civil_engineer', label: 'Civil Engineer', required: true },
  { id: 'site_supervisor', label: 'Site Supervisor', required: true },
  { id: 'safety_officer', label: 'Safety Officer', required: true },
  { id: 'procurement_officer', label: 'Procurement Officer', required: false }
];

// Sample team members data (in real app, this would come from an API)
const AVAILABLE_TEAM_MEMBERS = [
  { id: 1, name: 'Abdullah', role: 'Project Manager', department: 'Management', status: 'active' },
  { id: 2, name: 'Mohammed', role: 'Architect', department: 'Design', status: 'active' },
  { id: 3, name: 'Mishari', role: 'Engineer', department: 'Engineering', status: 'active' },
  { id: 4, name: 'Khairo', role: 'Site Supervisor', department: 'Construction', status: 'active' },
  { id: 5, name: 'Ahmed', role: 'Safety Officer', department: 'Safety', status: 'active' },
  { id: 6, name: 'Layla', role: 'Procurement Officer', department: 'Procurement', status: 'active' }
];

// Base Components
const GlassCard = ({ children, className = '', animate = false }) => (
  <div 
    className={`
      bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg 
      border border-white/20 transition-all duration-300
      ${animate ? 'hover:shadow-xl hover:scale-[1.02]' : ''}
      ${className}
    `}
  >
    {children}
  </div>
);

const FormProgressIndicator = ({ steps, currentStep, onStepClick, errors = {} }) => {
  return (
    <div className="mb-7 relative">
      <div className="flex justify-between">
        {/* Base Line for visual connection */}
        <div className="absolute top-1/3 left-[40px] right-[40px] h-1 bg-gray-200 transform -translate-y-1/2 z-0" />

        {/* Dynamic Progress Line */}
        <div 
          className="absolute top-1/3 left-[40px] h-1 bg-indigo-500 transform -translate-y-1/2 z-10"
          style={{ 
            width: `calc(${((currentStep - 1) / (steps.length - 1)) * 100}% - 80px)`, // Subtract total margins
            transition: 'width 0.6s ease-out'
          }}
        />

        {/* Steps */}
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          const hasErrors = errors[step.toLowerCase().replace(/\s+/g, '_')];

          return (
            <div
              key={step}
              onClick={() => onStepClick(stepNumber)}
              className="relative flex flex-col items-center cursor-pointer z-20"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-4
                ${hasErrors ? 'border-red-500 bg-red-100 text-red-600' :
                  isCompleted ? 'border-green-500 bg-green-500 text-white' :
                  isCurrent ? 'border-indigo-700 bg-white text-indigo-600' :
                  'border-gray-300 bg-white text-gray-500'}
                transition-all duration-300 ease-in-out shadow-md hover:shadow-lg`}
              >
                {isCompleted ? (
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                ) : (
                  <span className="font-semibold">{stepNumber}</span>
                )}
              </div>
              <div className={`mt-2 text-sm font-medium text-center
                ${hasErrors ? 'text-red-600' :
                  isCurrent ? 'text-indigo-700' :
                  isCompleted ? 'text-green-500' : 'text-gray-600'} transition-colors duration-300`}
              >
                {step}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};



const FormInput = ({ 
  label, 
  error, 
  required, 
  className = '', 
  icon: Icon,
  ...props 
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
      )}
      <input
        {...props}
        className={`
          w-full rounded-lg border transition-all duration-200
          ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5
          ${error 
            ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
            : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
          }
        `}
      />
    </div>
    {error && (
      <p className="mt-1 text-sm text-red-500 flex items-center">
        <AlertCircle className="w-4 h-4 mr-1" />
        {error}
      </p>
    )}
  </div>
);
const FormSelect = ({
  label,
  error,
  required,
  options,
  className = '',
  icon: Icon,
  ...props
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
      )}
      <select
        {...props}
        className={`
          w-full rounded-lg border transition-all duration-200
          ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5 appearance-none
          ${error 
            ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
            : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
          }
        `}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
        <ChevronDown className="h-5 w-5 text-gray-400" />
      </div>
    </div>
    {error && (
      <p className="mt-1 text-sm text-red-500 flex items-center">
        <AlertCircle className="w-4 h-4 mr-1" />
        {error}
      </p>
    )}
  </div>
);

const FormTextarea = ({
  label,
  error,
  required,
  className = '',
  ...props
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      {...props}
      className={`
        w-full rounded-lg border transition-all duration-200
        px-4 py-2.5 resize-none
        ${error 
          ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
          : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
        }
      `}
    />
    {error && (
      <p className="mt-1 text-sm text-red-500 flex items-center">
        <AlertCircle className="w-4 h-4 mr-1" />
        {error}
      </p>
    )}
  </div>
);

const FormFileUpload = ({ onUpload, accept = "*", multiple = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploads, setUploads] = useState([]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const newUploads = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      progress: 0,
      status: 'uploading'
    }));
    
    setUploads(prev => [...prev, ...newUploads]);

    newUploads.forEach(upload => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 5;
        if (progress > 100) {
          clearInterval(interval);
          return;
        }
        setUploads(prev => prev.map(u => 
          u.id === upload.id ? { ...u, progress } : u
        ));

        if (progress === 100) {
          setUploads(prev => prev.map(u => 
            u.id === upload.id ? { ...u, status: 'complete' } : u
          ));
          onUpload(upload.file);
          
          setTimeout(() => {
            setUploads(prev => prev.filter(u => u.id !== upload.id));
          }, 2000);
        }
      }, 100);
    });
  }, [onUpload]);

  return (
    <div className="space-y-4">
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          relative rounded-xl border-2 border-dashed p-6
          transition-all duration-200 group cursor-pointer
          ${isDragging
            ? 'border-indigo-400 bg-indigo-50'
            : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
          }
        `}
      >
        <input
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => {
            if (e.target.files?.length) {
              handleDrop({ preventDefault: () => {}, stopPropagation: () => {}, dataTransfer: { files: e.target.files } });
            }
          }}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
        />
        <div className="text-center">
          <Upload className={`
            w-10 h-10 mx-auto mb-3 transition-colors duration-200
            ${isDragging ? 'text-indigo-500' : 'text-gray-400 group-hover:text-indigo-500'}
          `} />
          <p className="text-sm font-medium text-gray-700">
            {isDragging ? "Drop files here" : "Click to upload or drag and drop"}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Maximum file size: 50MB
          </p>
        </div>
      </div>

      {uploads.map(upload => (
        <div
          key={upload.id}
          className="bg-white rounded-lg border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {upload.file.name}
                </p>
                <p className="text-xs text-gray-500">
                  {(upload.file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            </div>
            {upload.status === 'complete' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            ) : (
              <div className="text-xs font-medium text-indigo-600">
                {upload.progress}%
              </div>
            )}
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div
              className={`h-full rounded-full transition-all duration-200 ${
                upload.status === 'complete' ? 'bg-emerald-500' : 'bg-indigo-600'
              }`}
              style={{ width: `${upload.progress}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const FormLocationPicker = ({ value, onChange, error }) => {
  const MapEvents = () => {
    useMapEvents({
      click(e) {
        onChange(e.latlng);
      },
    });
    return null;
  };

  return (
    <div className="space-y-2">
      <div className={`
        h-[400px] rounded-xl overflow-hidden border transition-all duration-200
        ${error ? 'border-red-300' : 'border-gray-200'}
      `}>
        <MapContainer 
          center={value || [24.7136, 46.6753]} 
          zoom={13} 
          className="h-full w-full"
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapEvents />
          {value && <Marker position={value} />}
        </MapContainer>
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-500 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </p>
      )}
    </div>
  );
};

const LoadingOverlay = ({ message = 'Loading...' }) => (
  <div className="fixed inset-0 bg-black/30 backdrop-blur-sm 
    flex items-center justify-center z-50">
    <div className="bg-white p-8 rounded-2xl shadow-xl text-center">
      <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent 
        rounded-full animate-spin mx-auto mb-4" />
      <p className="text-gray-600">{message}</p>
    </div>
  </div>
);

const SuccessAnimation = ({ message = 'Success!', onClose }) => (
  <div className="fixed inset-0 bg-black/30 backdrop-blur-sm 
    flex items-center justify-center z-50">
    <div className="bg-white p-8 rounded-2xl shadow-xl text-center">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center 
        justify-center mx-auto mb-4">
        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
      </div>
      <p className="text-gray-800 font-medium mb-4">{message}</p>
      <button
        onClick={onClose}
        className="px-4 py-2 bg-emerald-600 text-white rounded-lg 
          hover:bg-emerald-700 transition-colors"
      >
        Continue
      </button>
    </div>
  </div>
);
const initialFormState = {
  basicInfo: {
    name: '',
    description: '',
    storeCode: '',
    priority: 'medium',
    startDate: '',
    endDate: '',
    projectType: 'renovation'
  },
  location: {
    siteType: '',
    address: '',
    city: '',
    district: '',
    coordinates: null,
    mapLink: '',
    buildingType: '',
    floorArea: '',
    nearbyLandmarks: [],
    attachments: []
  },
  budget: {
    totalBudget: '',
    currency: 'SAR',
    categories: {
      construction: '',
      design: '',
      equipment: '',
      permits: '',
      contingency: ''
    },
    fundingSource: '',
    budgetOwner: '',
    notes: ''
  },
  team: {
    projectManager: '',
    teamMembers: [],
    stakeholders: [],
    contractors: []
  },
  timeline: {
    phases: [
      {
        name: 'Planning',
        startDate: '',
        endDate: '',
        deliverables: [],
        milestones: []
      },
      {
        name: 'Design',
        startDate: '',
        endDate: '',
        deliverables: [],
        milestones: []
      },
      {
        name: 'Construction',
        startDate: '',
        endDate: '',
        deliverables: [],
        milestones: []
      },
      {
        name: 'Handover',
        startDate: '',
        endDate: '',
        deliverables: [],
        milestones: []
      }
    ],
    dependencies: []
  }
};

const FormSection = ({ title, description, children, className = '' }) => (
  <div className={className}>
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      )}
    </div>
    {children}
  </div>
);

const FormSiteTypeCard = ({ type, selected, onSelect }) => {
  const Icon = type.icon;
  
  return (
    <button
      onClick={() => onSelect(type.id)}
      className={`
        w-full p-4 rounded-xl border-2 transition-all duration-200
        ${selected 
          ? 'border-indigo-600 bg-indigo-50 shadow-md scale-[1.02]' 
          : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
        }
      `}
    >
      <div className="flex items-start gap-4">
        <div className={`
          p-3 rounded-xl transition-colors
          ${selected ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}
        `}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 text-left">
          <h3 className={`
            font-medium mb-1
            ${selected ? 'text-indigo-600' : 'text-gray-900'}
          `}>
            {type.label}
          </h3>
          <p className="text-sm text-gray-500">{type.description}</p>
        </div>
        {selected && (
          <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-1" />
        )}
      </div>
    </button>
  );
};
const TeamMemberCard = ({ member, onSelect }) => (
  <button
    onClick={() => onSelect(member)}
    className="w-full text-left transition-all duration-200
      bg-white hover:bg-gray-50 rounded-xl border border-gray-200 p-4
      hover:border-indigo-200 hover:shadow-sm"
  >
    <div className="flex items-center gap-4">
      <div className="relative">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 
          flex items-center justify-center">
          <span className="text-lg font-semibold text-indigo-600">
            {member.name[0]}
          </span>
        </div>
      </div>
      <div>
        <h4 className="font-medium text-gray-900">{member.name}</h4>
        <p className="text-sm text-gray-500">{member.role}</p>
        <p className="text-xs text-gray-400 mt-1">{member.department}</p>
      </div>
    </div>
  </button>
);
const TeamMemberSelectionModal = ({ show, onClose, onSelect, role }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');

  const departments = ['All', ...new Set(AVAILABLE_TEAM_MEMBERS.map(m => m.department))];

  const filteredMembers = AVAILABLE_TEAM_MEMBERS.filter(member => 
    (selectedDepartment === 'All' || member.department === selectedDepartment) &&
    (member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     member.role.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Select {role.label}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="mt-4 flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search team members..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg
                  focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg
                focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-1 gap-4">
            {filteredMembers.map(member => (
              <TeamMemberCard
                key={member.id}
                member={member}
                onSelect={() => onSelect(member)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Load draft from localStorage if available
  useEffect(() => {
    const savedDraft = localStorage.getItem('projectFormDraft');
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        setFormData(parsedDraft);
        setIsDraft(true);
      } catch (e) {
        console.error('Error loading draft:', e);
      }
    }
  }, []);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem('projectFormDraft', JSON.stringify(formData));
      setLastSaved(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, [formData]);

  // Form validation logic
  const validateStep = useCallback((step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Basic Info
        if (!formData.basicInfo.name) {
          newErrors.name = 'Project name is required';
        }
        if (!formData.basicInfo.storeCode) {
          newErrors.storeCode = 'Store code is required';
        }
        if (!formData.basicInfo.startDate) {
          newErrors.startDate = 'Start date is required';
        }
        if (!formData.basicInfo.endDate) {
          newErrors.endDate = 'End date is required';
        }
        if (formData.basicInfo.startDate && formData.basicInfo.endDate &&
            new Date(formData.basicInfo.startDate) >= new Date(formData.basicInfo.endDate)) {
          newErrors.endDate = 'End date must be after start date';
        }
        break;

      case 2: // Location
        if (!formData.location.siteType) {
          newErrors.siteType = 'Site type is required';
        }
        if (!formData.location.address) {
          newErrors.address = 'Address is required';
        }
        if (!formData.location.city) {
          newErrors.city = 'City is required';
        }
        if (!formData.location.coordinates) {
          newErrors.coordinates = 'Location on map is required';
        }
        break;

      case 3: // Budget
        if (!formData.budget.totalBudget) {
          newErrors.totalBudget = 'Total budget is required';
        } else if (isNaN(formData.budget.totalBudget) || formData.budget.totalBudget <= 0) {
          newErrors.totalBudget = 'Please enter a valid budget amount';
        }

        // Validate budget allocations
        const totalAllocation = BUDGET_CATEGORIES.reduce((total, category) => {
          const amount = formData.budget.categories[category.id];
          return total + (amount ? parseFloat(amount) : 0);
        }, 0);

        if (totalAllocation > parseFloat(formData.budget.totalBudget)) {
          newErrors.budget_allocation = 'Total allocated budget exceeds the total budget';
        }
        break;

      case 4: // Team
        const requiredRoles = TEAM_ROLES.filter(role => role.required);
        requiredRoles.forEach(role => {
          if (!formData.team.teamMembers.some(m => m.role === role.id)) {
            newErrors[`role_${role.id}`] = `${role.label} is required`;
          }
        });
        break;

      case 5: // Timeline
        formData.timeline.phases.forEach((phase, index) => {
          if (!phase.startDate) {
            newErrors[`phase${index}StartDate`] = 'Start date is required';
          }
          if (!phase.endDate) {
            newErrors[`phase${index}EndDate`] = 'End date is required';
          }
          if (phase.startDate && phase.endDate &&
              new Date(phase.startDate) >= new Date(phase.endDate)) {
            newErrors[`phase${index}EndDate`] = 'End date must be after start date';
          }

          // Validate phase dates are within project timeline
          if (phase.startDate && formData.basicInfo.startDate &&
              new Date(phase.startDate) < new Date(formData.basicInfo.startDate)) {
            newErrors[`phase${index}StartDate`] = 'Phase cannot start before project start date';
          }
          if (phase.endDate && formData.basicInfo.endDate &&
              new Date(phase.endDate) > new Date(formData.basicInfo.endDate)) {
            newErrors[`phase${index}EndDate`] = 'Phase cannot end after project end date';
          }
        });
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle step navigation
  const handleStepChange = useCallback((step) => {
    const currentStepValid = validateStep(currentStep);
    if (currentStepValid || step < currentStep) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, validateStep]);

  const handleSubmit = useCallback(async () => {
    // Validate all steps
    let isValid = true;
    for (let step = 1; step <= FORM_STEPS.length; step++) {
      if (!validateStep(step)) {
        isValid = false;
        break;
      }
    }

    if (!isValid) {
      alert('Please fill in all required fields before submitting');
      return;
    }

    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Clear draft after successful submission
      localStorage.removeItem('projectFormDraft');
      setIsDraft(false);
      
      setShowSuccess(true);
    } catch (error) {
      console.error('Error saving project:', error);
      alert('An error occurred while saving the project. Please try again.');
    }
    setLoading(false);
  }, [validateStep]);
// ... continuing from Part 3

const renderBasicInfo = () => (
  <FormSection
    title="Basic Information"
    description="Provide the fundamental details about your project"
  >
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <FormInput
        label="Project Name"
        required
        value={formData.basicInfo.name}
        onChange={(e) => setFormData(prev => ({
          ...prev,
          basicInfo: { ...prev.basicInfo, name: e.target.value }
        }))}
        error={errors.name}
        placeholder="Enter project name"
      />

      <FormInput
        label="Store Code"
        required
        value={formData.basicInfo.storeCode}
        onChange={(e) => setFormData(prev => ({
          ...prev,
          basicInfo: { ...prev.basicInfo, storeCode: e.target.value }
        }))}
        error={errors.storeCode}
        placeholder="Enter store code"
      />

      <div className="col-span-2">
        <FormTextarea
          label="Description"
          value={formData.basicInfo.description}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            basicInfo: { ...prev.basicInfo, description: e.target.value }
          }))}
          placeholder="Describe your project"
          rows={4}
        />
      </div>

      <FormSelect
        label="Priority Level"
        options={PRIORITY_OPTIONS}
        value={formData.basicInfo.priority}
        onChange={(e) => setFormData(prev => ({
          ...prev,
          basicInfo: { ...prev.basicInfo, priority: e.target.value }
        }))}
      />

      <FormSelect
        label="Project Type"
        options={PROJECT_TYPES}
        value={formData.basicInfo.projectType}
        onChange={(e) => setFormData(prev => ({
          ...prev,
          basicInfo: { ...prev.basicInfo, projectType: e.target.value }
        }))}
      />

      <FormInput
        type="date"
        label="Start Date"
        required
        value={formData.basicInfo.startDate}
        onChange={(e) => setFormData(prev => ({
          ...prev,
          basicInfo: { ...prev.basicInfo, startDate: e.target.value }
        }))}
        error={errors.startDate}
      />

      <FormInput
        type="date"
        label="End Date"
        required
        value={formData.basicInfo.endDate}
        onChange={(e) => setFormData(prev => ({
          ...prev,
          basicInfo: { ...prev.basicInfo, endDate: e.target.value }
        }))}
        error={errors.endDate}
      />
    </div>
  </FormSection>
);

const renderLocation = () => (
  <FormSection
    title="Location Details"
    description="Specify where your project will be located"
  >
    <div className="space-y-8">
      {/* Site Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SITE_TYPES.map(type => (
          <FormSiteTypeCard
            key={type.id}
            type={type}
            selected={formData.location.siteType === type.id}
            onSelect={(id) => setFormData(prev => ({
              ...prev,
              location: { ...prev.location, siteType: id }
            }))}
          />
        ))}
      </div>
      {errors.siteType && (
        <p className="text-sm text-red-500 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {errors.siteType}
        </p>
      )}

      {/* Address Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="col-span-2">
          <FormTextarea
            label="Address"
            required
            value={formData.location.address}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              location: { ...prev.location, address: e.target.value }
            }))}
            error={errors.address}
            placeholder="Enter full address"
            rows={2}
          />
        </div>

        <FormInput
          label="City"
          required
          value={formData.location.city}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            location: { ...prev.location, city: e.target.value }
          }))}
          error={errors.city}
          placeholder="Enter city"
        />

        <FormInput
          label="District"
          value={formData.location.district}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            location: { ...prev.location, district: e.target.value }
          }))}
          placeholder="Enter district"
        />
      </div>

      {/* Map Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Location on Map *
        </label>
        <FormLocationPicker
          value={formData.location.coordinates}
          onChange={(coords) => setFormData(prev => ({
            ...prev,
            location: { ...prev.location, coordinates: coords }
          }))}
          error={errors.coordinates}
        />
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Location Documents
        </label>
        <FormFileUpload
          onUpload={(files) => setFormData(prev => ({
            ...prev,
            location: { 
              ...prev.location, 
              attachments: [...prev.location.attachments, ...files] 
            }
          }))}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          multiple
        />
      </div>
    </div>
  </FormSection>
);

const renderBudget = () => (
  <FormSection
    title="Budget Information"
    description="Define the financial aspects of your project"
  >
    <div className="space-y-8">
      {/* Total Budget */}
      <div className="flex gap-4">
        <FormSelect
          label="Currency"
          options={[
            { value: 'SAR', label: 'SAR' },
            { value: 'USD', label: 'USD' },
            { value: 'EUR', label: 'EUR' }
          ]}
          value={formData.budget.currency}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            budget: { ...prev.budget, currency: e.target.value }
          }))}
          className="w-32"
        />

        <FormInput
          label="Total Budget"
          required
          type="number"
          value={formData.budget.totalBudget}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            budget: { ...prev.budget, totalBudget: e.target.value }
          }))}
          error={errors.totalBudget}
          placeholder="Enter total budget"
          className="flex-1"
        />
      </div>

      {/* Budget Categories */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Budget Allocation</h3>
        <div className="space-y-4">
          {BUDGET_CATEGORIES.map(category => {
            const amount = formData.budget.totalBudget 
              ? (category.percentage / 100 * parseFloat(formData.budget.totalBudget)).toFixed(2)
              : '0.00';

            return (
              <div key={category.id} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {category.label} ({category.percentage}%)
                  </label>
                  <span className="text-sm text-gray-500">
                    {formData.budget.currency} {amount}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${category.percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional Budget Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormInput
          label="Funding Source"
          value={formData.budget.fundingSource}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            budget: { ...prev.budget, fundingSource: e.target.value }
          }))}
          placeholder="Enter funding source"
        />

        <FormInput
          label="Budget Owner"
          value={formData.budget.budgetOwner}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            budget: { ...prev.budget, budgetOwner: e.target.value }
          }))}
          placeholder="Enter budget owner name"
        />
      </div>

      <FormTextarea
        label="Budget Notes"
        value={formData.budget.notes}
        onChange={(e) => setFormData(prev => ({
          ...prev,
          budget: { ...prev.budget, notes: e.target.value }
        }))}
        placeholder="Add any additional budget-related notes"
        rows={4}
      />
    </div>
  </FormSection>
);

const renderTeam = () => (
  <FormSection
    title="Team Assignment"
    description="Assign team members to required roles"
  >
    <div className="space-y-8">
      {/* Required Roles */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Required Roles</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TEAM_ROLES.filter(role => role.required).map(role => {
            const assignedMember = formData.team.teamMembers.find(m => m.role === role.id);
            const error = errors[`role_${role.id}`];

            return (
              <div 
                key={role.id}
                className={`
                  p-4 rounded-xl border-2 transition-all duration-200
                  ${error 
                    ? 'border-red-200 bg-red-50'
                    : assignedMember
                      ? 'border-emerald-200 bg-emerald-50'
                      : 'border-gray-200'
                  }
                `}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900">{role.label}</h4>
                    {error && (
                      <p className="text-sm text-red-500 mt-1">{error}</p>
                    )}
                  </div>
                  {assignedMember && (
                    <button
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          team: {
                            ...prev.team,
                            teamMembers: prev.team.teamMembers.filter(m => m.role !== role.id)
                          }
                        }));
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {assignedMember ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-100 to-violet-100 
                      flex items-center justify-center">
                      <span className="text-lg font-semibold text-indigo-600">
                        {assignedMember.name[0]}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{assignedMember.name}</h4>
                      <p className="text-sm text-gray-500">{assignedMember.role}</p>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setSelectedRole(role);
                      setShowTeamModal(true);
                    }}
                    className="w-full py-2 text-sm text-indigo-600 bg-indigo-50 
                      rounded-lg hover:bg-indigo-100 transition-colors flex items-center 
                      justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Assign Team Member
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Optional Roles */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Optional Roles</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TEAM_ROLES.filter(role => !role.required).map(role => {
            const assignedMember = formData.team.teamMembers.find(m => m.role === role.id);

            return (
              <div 
                key={role.id}
                className="p-4 rounded-xl border-2 border-gray-200"
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-gray-900">{role.label}</h4>
                  {assignedMember && (
                    <button
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          team: {
                            ...prev.team,
                            teamMembers: prev.team.teamMembers.filter(m => m.role !== role.id)
                          }
                        }));
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {assignedMember ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-100 to-violet-100 
                      flex items-center justify-center">
                      <span className="text-lg font-semibold text-indigo-600">
                        {assignedMember.name[0]}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{assignedMember.name}</h4>
                      <p className="text-sm text-gray-500">{assignedMember.role}</p>
                    </div>
                  </div>
                ) : (
                  <button
                  onClick={() => {
                    setSelectedRole(role);
                    setShowTeamModal(true);
                  }}
                  className="w-full py-2 text-sm text-gray-600 bg-gray-50 
                    rounded-lg hover:bg-gray-100 transition-colors flex items-center 
                    justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Assign Team Member
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>
</FormSection>
);

const renderTimeline = () => (
<FormSection
  title="Project Timeline"
  description="Define the project phases and their timelines"
>
  <div className="space-y-6">
    {formData.timeline.phases.map((phase, index) => (
      <div
        key={index}
        className="bg-gray-50 rounded-xl p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{phase.name}</h3>
          <select
            value={phase.status || 'not-started'}
            onChange={(e) => {
              const updatedPhases = [...formData.timeline.phases];
              updatedPhases[index] = {
                ...phase,
                status: e.target.value
              };
              setFormData(prev => ({
                ...prev,
                timeline: { ...prev.timeline, phases: updatedPhases }
              }));
            }}
            className="px-3 py-1.5 rounded-lg text-sm border border-gray-300
              focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="not-started">Not Started</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="delayed">Delayed</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            type="date"
            label="Start Date"
            required
            value={phase.startDate}
            onChange={(e) => {
              const updatedPhases = [...formData.timeline.phases];
              updatedPhases[index] = {
                ...phase,
                startDate: e.target.value
              };
              setFormData(prev => ({
                ...prev,
                timeline: { ...prev.timeline, phases: updatedPhases }
              }));
            }}
            error={errors[`phase${index}StartDate`]}
          />

          <FormInput
            type="date"
            label="End Date"
            required
            value={phase.endDate}
            onChange={(e) => {
              const updatedPhases = [...formData.timeline.phases];
              updatedPhases[index] = {
                ...phase,
                endDate: e.target.value
              };
              setFormData(prev => ({
                ...prev,
                timeline: { ...prev.timeline, phases: updatedPhases }
              }));
            }}
            error={errors[`phase${index}EndDate`]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Deliverables
          </label>
          <div className="space-y-2">
            {phase.deliverables.map((deliverable, dIndex) => (
              <div key={dIndex} className="flex items-center gap-2">
                <FormInput
                  value={deliverable}
                  onChange={(e) => {
                    const updatedPhases = [...formData.timeline.phases];
                    updatedPhases[index].deliverables[dIndex] = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      timeline: { ...prev.timeline, phases: updatedPhases }
                    }));
                  }}
                  placeholder="Enter deliverable"
                  className="flex-1"
                />
                <button
                  onClick={() => {
                    const updatedPhases = [...formData.timeline.phases];
                    updatedPhases[index].deliverables = phase.deliverables.filter(
                      (_, i) => i !== dIndex
                    );
                    setFormData(prev => ({
                      ...prev,
                      timeline: { ...prev.timeline, phases: updatedPhases }
                    }));
                  }}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                const updatedPhases = [...formData.timeline.phases];
                updatedPhases[index].deliverables.push('');
                setFormData(prev => ({
                  ...prev,
                  timeline: { ...prev.timeline, phases: updatedPhases }
                }));
              }}
              className="text-sm text-indigo-600 hover:text-indigo-700 
                font-medium flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add Deliverable
            </button>
          </div>
        </div>
      </div>
    ))}
  </div>
</FormSection>
);

const renderCurrentStep = () => {
switch (currentStep) {
  case 1:
    return renderBasicInfo();
  case 2:
    return renderLocation();
  case 3:
    return renderBudget();
  case 4:
    return renderTeam();
  case 5:
    return renderTimeline();
  default:
    return null;
}
};



return (
<div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
  <div className="max-w-5xl mx-auto px-4">
    {/* Header */}
    <div className="mb-8">
      <div className="flex items-center gap-4 mb-2">
        <button
          onClick={() => navigate('/projects')}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {id ? 'Edit Project' : 'Create New Project'}
        </h1>
      </div>
      <p className="text-gray-600">
        {id ? 'Update the project details below' : 'Fill in the project information to get started'}
      </p>

      {isDraft && lastSaved && (
        <div className="mt-2 text-sm text-gray-500">
          Draft last saved at: {lastSaved.toLocaleTimeString()}
        </div>
      )}
    </div>

    {/* Progress Indicator */}
    <FormProgressIndicator
      steps={FORM_STEPS}
      currentStep={currentStep}
      onStepClick={handleStepChange}
      errors={errors}
    />

    {/* Form Content */}
    <GlassCard className="p-8 mb-8">
      {renderCurrentStep()}
    </GlassCard>

    {/* Navigation Buttons */}
    <div className="flex justify-between">
      <button
        onClick={() => handleStepChange(currentStep - 1)}
        disabled={currentStep === 1}
        className="px-6 py-2 text-gray-600 hover:text-gray-800 
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors"
      >
        Previous
      </button>
      <button
        onClick={() => {
          if (currentStep === FORM_STEPS.length) {
            handleSubmit();
          } else {
            handleStepChange(currentStep + 1);
          }
        }}
        className="px-6 py-2 bg-indigo-600 text-white rounded-lg 
          hover:bg-indigo-700 transition-colors shadow-sm hover:shadow-md
          disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {currentStep === FORM_STEPS.length ? 'Create Project' : 'Next'}
      </button>
    </div>

    

    {/* Team Member Selection Modal */}
    {showTeamModal && selectedRole && (
      <TeamMemberSelectionModal
        show={true}
        onClose={() => {
          setShowTeamModal(false);
          setSelectedRole(null);
        }}
        onSelect={(member) => {
          setFormData(prev => ({
            ...prev,
            team: {
              ...prev.team,
              teamMembers: [
                ...prev.team.teamMembers.filter(m => m.role !== selectedRole.id),
                { ...member, role: selectedRole.id }
              ]
            }
          }));
          setShowTeamModal(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
      />
    )}

    {/* Loading Overlay */}
    {loading && (
      <LoadingOverlay message="Creating project..." />
    )}

    {/* Success Animation */}
    {showSuccess && (
      <SuccessAnimation
        message="Project created successfully!"
        onClose={() => {
          setShowSuccess(false);
          navigate('/projects');
        }}
      />
    )}
  </div>
</div>
);
};

export default ProjectForm;